import React from 'react';
import Phaser from 'phaser';
import { Box } from '@mui/material';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import { TimingOffsetsConfig } from 'Models/EventStream';
import TimeSignature from 'Models/TimeSignature';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import Scheduler2 from 'Utils/Scheduler2';
import { PhaserGame, type IRefPhaserGame } from 'Phaser/PhaserGame';
import { PhaserToReactListeners, NotifyPhaserEvents, RemoveListener } from 'Phaser/EventBus2';
import { useMidiContext, type NoteMessage } from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import type {
  ExerciseSessionControls,
  OSMDExerciseSessionInput,
} from 'Phaser/Scenes/OSMDExerciseScene';
import { resolveExerciseSceneKey } from 'Phaser/Scenes/exerciseSceneBinding';
import type { InteractionFamily } from 'Types/ExerciseContentTypes';
import type { ExerciseSessionEmitted } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import { useDispatch, useSelector } from 'react-redux';
import type { MainAppReducer } from 'Types';
import logger from 'Utils/Logger';
import {
  playthroughCompleted,
  pointEarned,
  sessionCompleted,
  sessionStarted,
  hydrateRequested,
  selectOpenStatus,
  selectPlaySessionId,
  type EntryContext,
} from 'Reducers/exerciseProgressReducer';
import type { SessionRecord } from 'Grading';
import { takeRecordOutbox } from 'Models/takeRecordOutbox';
import { linkedToSitting } from './linkedToSitting';
import { selectKeySignatureDisplay } from 'NotationDisplay/keySignatureDisplaySlice';
import { selectScaleFingerNumbers } from 'NotationDisplay/scaleFingerNumbersSlice';
import { RUN_METER } from 'Phaser/Scenes/OSMDExerciseScene.runScore';
import { getCurrentAppVersion } from 'Utils/appVersion';
import { useExerciseSessionReturnNavigation } from 'Hooks/useExerciseSessionReturnNavigation';

/**
 * The shared core of a played exercise sitting (MUS-481). Both the real,
 * user-facing play screen and the dev launcher mount the identical machinery -
 * the Phaser scene, the session-machine Redux bridge, the host MIDI bridge
 * (MUS-319), the playback scheduler injection (MUS-553), and the
 * return-to-roadmap navigation (MUS-321). This component is
 * the single home for that machinery so there is exactly one copy of each: a
 * second, drifting copy of the MIDI bridge or the return-nav is the regression
 * this extraction exists to prevent.
 *
 * It owns none of the control chrome. The caller renders that via `children`, a
 * render-prop handed the live `ExerciseSessionControls` (null until the scene is
 * ready). The real screen draws real Start / Submit / End; the dev launcher draws
 * its debug panel. The shell carries no write-state / frontier debug readouts.
 *
 * Gated on USE_EXERCISES: the scene is only registered when the flag is on, and
 * the MIDI bridge and return-nav are dark until then, so a shell mounted with the
 * flag off renders nothing playable.
 */

const GAME_WIDTH = 960;
const GAME_HEIGHT = 400;

/**
 * The sitting to launch: figure + exercise identity + content coordinate + config,
 * plus the interaction family that binds the sitting to a playable scene (MUS-456).
 * The first four fields are the scene's own input; `interaction_family` is content
 * metadata the shell resolves the scene from, so it is not part of the scene input.
 */
export type ExercisePlaySessionInput = Pick<
  OSMDExerciseSessionInput,
  'prompt' | 'exercise' | 'cell' | 'config' | 'materialKey'
> & {
  interaction_family: InteractionFamily;
  /** The level and tier the sitting was started from, sent on the open (none from the dev launcher). */
  entryContext?: EntryContext | null;
};

/** Ends the sitting through the scene's controls; a scene already torn down is logged, not thrown. */
function endSitting(controls: ExerciseSessionControls | null): void {
  try {
    controls?.end();
  } catch (error) {
    logger.warn('[ExercisePlayShell] the sitting could not be ended on leaving:', error);
  }
}

interface ExercisePlayShellProps {
  session: ExercisePlaySessionInput;
  /**
   * Optional observation seam over the machine's emitted events, in addition to
   * the internal Redux bridge that always runs. The dev launcher uses it to keep
   * an at-a-glance event log; the real screen passes nothing.
   */
  onSessionEvent?: (event: ExerciseSessionEmitted) => void;
  /**
   * A take ended on its own (the music ended and it was graded) or was stopped:
   * the real screen uses it to offer Next, since no Submit tap ends a take.
   */
  onTakeEnded?: () => void;
  /** Render-prop for control chrome, handed the live controls (null until ready). */
  children?: (controls: ExerciseSessionControls | null) => React.ReactNode;
  /**
   * Spread onto the outer container - lets a caller attach its own data hooks.
   * The `data-*` index signature is what makes a `{ 'data-...': ... }` literal
   * type-check here: MUI's Box props accept data attributes as bare JSX props but
   * not through a plain props object without it.
   */
  containerProps?: React.ComponentProps<typeof Box> & {
    [dataAttr: `data-${string}`]: string | number | boolean | undefined;
  };
}

const ExercisePlayShell: React.FC<ExercisePlayShellProps> = ({
  session,
  onSessionEvent,
  onTakeEnded,
  children,
  containerProps,
}) => {
  const enabled = useFeatureFlag(FeatureFlag.USE_EXERCISES);
  const dispatch = useDispatch();
  // MUS-319: the exercise scene inherits the base MidiOn/MidiOff listeners, but the
  // host has to produce those events. Bridge the app's MIDI stream (Web MIDI on
  // web, the native/Bluetooth bridge on device) into Phaser, mirroring PianoDebug.
  const { addListener, removeListener } = useMidiContext();
  // Return-to-roadmap seam (MUS-321): on sessionCompleted the slice parks the
  // summary; this hook navigates to /exercises off that edge. Flag-guarded.
  useExerciseSessionReturnNavigation();

  const phaserRef = React.useRef<IRefPhaserGame | null>(null);
  const [controls, setControls] = React.useState<ExerciseSessionControls | null>(null);
  // The scene's own controls, for ending the sitting when the screen goes away.
  const controlsRef = React.useRef<ExerciseSessionControls | null>(null);
  const [timeKeeper] = React.useState(() => createTimeKeeper());
  const [eventStream] = React.useState(
    () => new OSMDEventStream(timeKeeper, new TimingOffsetsConfig(), null, 1, 0.025, 0.1, true),
  );

  // MUS-553: the base scene's playback machine (count-in, cursor, note judging) is
  // only instantiated when the host injects a Scheduler2 - the same seam the
  // repertoire container uses. Without it every transport event is dropped and the
  // TimeKeeper never unpauses. The scheduler must share the event stream's
  // TimeKeeper. Created lazily on first injection and destroyed on unmount, so a
  // StrictMode mount/unmount/mount never leaves the live scene holding a destroyed
  // scheduler.
  //
  // The user's metronome sound preference applies to the scheduler's
  // post-count-in clicks (the scene's own count-in click reads the same flag),
  // mirroring the repertoire play context. Default on when unset.
  const metronomeSound = useSelector(
    (state: MainAppReducer) => state.mainAppReducer?.userData?.rep_metronome_sound ?? true,
  );
  // Kept only to hand the accented-downbeat preference back when the screen goes away (on the
  // native apps the downbeat switch is the audio engine's, shared with every surface).
  const downbeatsSound = useSelector(
    (state: MainAppReducer) => state.mainAppReducer?.userData?.rep_downbeats_sound ?? true,
  );
  const soundPrefsRef = React.useRef({ metronomeSound, downbeatsSound });
  soundPrefsRef.current = { metronomeSound, downbeatsSound };

  // The take record reads these when it needs them: the sitting's play-session id once
  // the open answers, and the app version (resolved in the background).
  const playSessionId = useSelector(selectPlaySessionId);
  // How scales are written: read when each run loads.
  const keySignatureDisplay = useSelector(selectKeySignatureDisplay);
  const keySignatureDisplayRef = React.useRef(keySignatureDisplay);
  keySignatureDisplayRef.current = keySignatureDisplay;
  // Whether the reference finger numbers are drawn: read when each run loads.
  const fingerNumbers = useSelector(selectScaleFingerNumbers);
  const fingerNumbersRef = React.useRef(fingerNumbers);
  fingerNumbersRef.current = fingerNumbers;
  const playSessionIdRef = React.useRef(playSessionId);
  playSessionIdRef.current = playSessionId;
  // The take records wait for the sitting's open, like its other writes: a record made while
  // the open is in flight is held (the latest one carries every take) and sent once it answers.
  const openStatus = useSelector(selectOpenStatus);
  const openStatusRef = React.useRef(openStatus);
  openStatusRef.current = openStatus;
  const heldRecordRef = React.useRef<SessionRecord | null>(null);
  // Set while the page is going away: the sitting's last record goes over keepalive.
  const tearingDownRef = React.useRef(false);
  const submitTakeRecord = React.useCallback((record: SessionRecord) => {
    if (tearingDownRef.current) {
      heldRecordRef.current = null;
      takeRecordOutbox.submitOnTeardown(linkedToSitting(record, playSessionIdRef.current));
    } else if (openStatusRef.current === 'pending') {
      heldRecordRef.current = record;
    } else {
      takeRecordOutbox.submit(linkedToSitting(record, playSessionIdRef.current));
    }
  }, []);
  React.useEffect(() => {
    const held = heldRecordRef.current;
    if (openStatus === 'pending' || held === null) return;
    heldRecordRef.current = null;
    takeRecordOutbox.submit(linkedToSitting(held, playSessionId));
  }, [openStatus, playSessionId]);
  const appVersionRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    let live = true;
    getCurrentAppVersion()
      .then((version) => {
        if (live) appVersionRef.current = version;
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const schedulerRef = React.useRef<Scheduler2 | null>(null);
  const getScheduler = React.useCallback(() => {
    if (schedulerRef.current === null) {
      const scheduler = new Scheduler2(timeKeeper);
      void scheduler.changeMetronomeAudio(soundPrefsRef.current.metronomeSound);
      // A scale run is unmetered: every click sounds the same, whatever the user's
      // accented-downbeat preference elsewhere.
      void scheduler.changeDownbeatsAudio(false);
      schedulerRef.current = scheduler;
    }
    return schedulerRef.current;
  }, [timeKeeper]);
  React.useEffect(() => {
    void schedulerRef.current?.changeMetronomeAudio(metronomeSound);
  }, [metronomeSound]);
  React.useEffect(
    () => () => {
      void schedulerRef.current?.changeDownbeatsAudio(soundPrefsRef.current.downbeatsSound);
      schedulerRef.current?.destroy();
      schedulerRef.current = null;
    },
    [],
  );

  // Latest session + observation seam, read through refs so the scene-launch
  // callback stays stable and does not re-fire on prop identity changes.
  const sessionRef = React.useRef(session);
  sessionRef.current = session;
  const onSessionEventRef = React.useRef(onSessionEvent);
  onSessionEventRef.current = onSessionEvent;
  const onTakeEndedRef = React.useRef(onTakeEnded);
  onTakeEndedRef.current = onTakeEnded;

  const startScene = React.useCallback(() => {
    if (!phaserRef.current?.game) return;

    const { prompt, exercise, cell, config, interaction_family, materialKey, entryContext } =
      sessionRef.current;

    // MUS-456: resolve the playable scene from the exercise's interaction family via
    // the scene-config binding, rather than starting a hardcoded scene key. A family
    // with no playable scene yet (manipulated / answered) resolves to null; guard
    // before any session side effect so nothing is dispatched for a scene that never
    // opens.
    const sceneKey = resolveExerciseSceneKey(interaction_family);
    if (sceneKey === null) {
      logger.warn(
        `[ExercisePlayShell] no playable scene bound for interaction family "${interaction_family}"; not launching`,
      );
      return;
    }

    // MUS-553: the TimeKeeper must know the meter before the transport can run -
    // TimeKeeper.unpause() silently returns (stays paused) when it has no time
    // signature, so the scheduler would start the machine but the clock, cursor and
    // note judging would never move. A scale run is unmetered: the transport counts
    // one quarter-note beat per hidden measure (the scene renders the same layout),
    // so the count-in and the metronome click every beat. Idempotent on restart.
    const [numerator, denominator] = RUN_METER;
    timeKeeper.setTimeSignatures(0, [new TimeSignature(numerator, denominator)]);

    // A sitting is opening: tell the slice which exercise it belongs to (the saga
    // attributes earned clears to it) and hydrate its existing frontier. Both are
    // dark behind USE_EXERCISES via the saga guard.
    dispatch(sessionStarted({ exerciseId: exercise.id, entryContext: entryContext ?? null }));
    dispatch(hydrateRequested({ exerciseId: exercise.id }));

    const exerciseSession: OSMDExerciseSessionInput = {
      prompt,
      exercise,
      cell,
      config,
      materialKey: materialKey ?? null,
      // Every exercise take is graded and recorded as a take record; each ended take
      // hands over the sitting's record, and the outbox sends what the server lacks.
      isClickOn: () => soundPrefsRef.current.metronomeSound,
      appVersion: () => appVersionRef.current,
      keySignatureDisplay: () => keySignatureDisplayRef.current,
      fingerNumbers: () => fingerNumbersRef.current,
      playSessionId: () => playSessionIdRef.current,
      onTakeRecorded: submitTakeRecord,
      // The engine's emitted facts become Redux actions: the saga relays pointEarned
      // to the backend and drives navigation off sessionCompleted; the slice carries
      // feedback + the frontier mirror. onSessionEvent is the optional caller seam.
      onSessionEvent: (event: ExerciseSessionEmitted) => {
        onSessionEventRef.current?.(event);
        switch (event.type) {
          case 'playthroughCompleted':
            dispatch(
              playthroughCompleted({
                cell: event.cell,
                config: event.config,
                accuracy: event.accuracy,
                verdict: event.verdict,
                playthroughIndex: event.playthroughIndex,
              }),
            );
            break;
          case 'pointEarned':
            dispatch(
              pointEarned({ cell: event.cell, config: event.config, mastered: event.mastered }),
            );
            break;
          case 'sessionCompleted':
            dispatch(sessionCompleted({ summary: event.summary }));
            break;
          default: {
            // Exhaustiveness guard: a new emitted variant fails to compile here until
            // it is handled, instead of being silently dropped from the Redux bridge.
            const _exhaustive: never = event;
            return _exhaustive;
          }
        }
      },
      onTakeEnded: () => onTakeEndedRef.current?.(),
      onReady: (sessionControls: ExerciseSessionControls) => {
        // MUS-553: inject the scheduler here, not right after scene.start. The
        // Phaser game is a reused singleton, so at scene.start time a previous
        // instance of this scene can still be alive with its listener registered;
        // an emit then would reach that dying instance and the restarted one would
        // boot with no scheduler. onReady runs in create(), after this instance's
        // init() registered its listener, so delivery is direct and to the live
        // scene. It precedes handing out the controls, so no user Start can reach
        // the transport before the machine is running.
        const scheduler = getScheduler();
        NotifyPhaserEvents.InjectScheduler2({ scheduler2: scheduler });
        controlsRef.current = sessionControls;
        setControls({
          ...sessionControls,
          start: () => {
            // MUS-553: initialize the scheduler's audio inside the Start gesture,
            // as the repertoire host does on its first Start. init() unlocks the
            // AudioContext (Tone.start must run in a user gesture) and decodes the
            // metronome click samples; without it the scheduler's post-count-in
            // metronome schedules silent no-ops. The samples are only needed after
            // the first played note, so the async decode is not awaited. Idempotent.
            scheduler
              .init({ tempo: sessionRef.current.config.tempo })
              .catch((error: unknown) =>
                logger.warn('[ExercisePlayShell] scheduler audio init failed:', error),
              );
            sessionControls.start();
          },
        });
      },
    };

    phaserRef.current.game.scene.start(sceneKey, {
      eventStream,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      performanceTempo: config.tempo,
      cursorVisible: true,
      scoreTheme: 'light',
      exerciseSession,
    });
  }, [eventStream, timeKeeper, dispatch, getScheduler, submitTakeRecord]);

  // The sitting ends when the play screen goes away: on unmount (the player navigated
  // elsewhere) and when the page is hidden for good (pagehide: the tab closed, the page
  // navigated away or went to the back-forward cache). Ending it records a take still open as
  // `left`, sends the sitting's record, and closes the sitting with its summary - over keepalive
  // on page hide, since a normal request may not outlive the page. Ending an ended sitting does
  // nothing, so a sitting the player ended is not ended twice.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const onPageHide = () => {
      tearingDownRef.current = true;
      endSitting(controlsRef.current);
      const held = heldRecordRef.current;
      if (held !== null) {
        heldRecordRef.current = null;
        takeRecordOutbox.submitOnTeardown(linkedToSitting(held, playSessionIdRef.current));
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      endSitting(controlsRef.current);
      controlsRef.current = null;
    };
  }, [enabled]);

  React.useEffect(() => {
    if (!enabled) return undefined;
    PhaserToReactListeners.PhaserReady(startScene, undefined, false, true);
    if (phaserRef.current?.wasReused) startScene();
    return () => {
      RemoveListener('PhaserReady', startScene);
    };
  }, [enabled, startScene]);

  // MUS-319: forward real MIDI note events into the Phaser scene. The scene's
  // inherited base handler ignores them until a take's count-in arms playback, then
  // scores each played note against the event stream that drives the graded bands.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const listener = {
      name: 'exercise-play-midi-bridge',
      noteOn: (ev: NoteMessage) => {
        if (ev.velocity === 0) {
          NotifyPhaserEvents.MidiOff({ note: ev.note });
          return;
        }
        NotifyPhaserEvents.MidiOn({ note: ev.note, velocity: ev.velocity });
      },
      noteOff: (ev: NoteMessage) => NotifyPhaserEvents.MidiOff({ note: ev.note }),
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [enabled, addListener, removeListener]);

  if (!enabled) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#F4E5C7',
      }}
      {...containerProps}
    >
      <style>{`#game-container { pointer-events: auto !important; }`}</style>
      <PhaserGame
        ref={phaserRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        scaling={Phaser.Scale.NONE}
        crisp
      />
      {children?.(controls)}
    </Box>
  );
};

export default ExercisePlayShell;
